import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Stack,
  useTheme,
  alpha,
  Fade,
  Grow,
  useMediaQuery,
} from '@mui/material';
import { Work, Code, Psychology, TrendingUp, Terminal } from '@mui/icons-material';

function TypingText({ text, delay = 0, speed = 100 }) {
  const [displayText, setDisplayText] = useState('');
  const [showCursor, setShowCursor] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      let i = 0;
      const typingInterval = setInterval(() => {
        if (i < text.length) {
          setDisplayText(text.slice(0, i + 1));
          i++;
        } else {
          clearInterval(typingInterval);
          setShowCursor(false);
        }
      }, speed);

      return () => clearInterval(typingInterval);
    }, delay);

    return () => clearTimeout(timer);
  }, [text, delay, speed]);

  return (
    <span>
      {displayText}
      {showCursor && <span className="terminal-cursor">|</span>}
    </span>
  );
}

function HeroSection({ stats }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [animationStarted, setAnimationStarted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setAnimationStarted(true), 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Box
      sx={{
        position: 'relative',
        py: { xs: 6, sm: 8, md: 12 },
        background: `
          radial-gradient(circle at 20% 80%, ${alpha('#00ffff', 0.03)} 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, ${alpha('#ff6b35', 0.03)} 0%, transparent 50%),
          radial-gradient(circle at 40% 40%, ${alpha('#00ff41', 0.02)} 0%, transparent 50%)
        `,
        overflow: 'hidden',
        minHeight: { xs: '60vh', sm: '70vh', md: '80vh' },
        display: 'flex',
        alignItems: 'center',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            linear-gradient(45deg, transparent 49%, ${alpha('#00ffff', 0.05)} 50%, transparent 51%),
            linear-gradient(-45deg, transparent 49%, ${alpha('#00ff41', 0.05)} 50%, transparent 51%)
          `,
          backgroundSize: '20px 20px',
          opacity: 0.3,
        },
      }}
    >
      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>
        <Box sx={{ textAlign: 'center', mb: { xs: 6, sm: 8 } }}>
          <Fade in={true} timeout={1000}>
            <Box sx={{ mb: { xs: 3, sm: 4 } }}>
              <Typography
                variant="h6"
                sx={{
                  color: '#00ffff',
                  fontFamily: '"Fira Code", monospace',
                  mb: 1,
                  opacity: 0.8,
                  fontSize: { xs: '0.9rem', sm: '1.25rem' },
                }}
              >
                $ whoami
              </Typography>
            </Box>
          </Fade>

          <Fade in={animationStarted} timeout={1500}>
            <Typography
              variant="h1"
              sx={{
                mb: 2,
                background: `linear-gradient(135deg, #00ffff 0%, #00ff41 50%, #ff6b35 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: 800,
                fontFamily: '"Fira Code", monospace',
                textShadow: '0 0 30px rgba(0, 255, 255, 0.5)',
                fontSize: { 
                  xs: '2rem', 
                  sm: '3rem', 
                  md: '4rem', 
                  lg: '6rem' 
                },
                lineHeight: { xs: 1.2, sm: 1.1 },
              }}
              className="glow-text"
            >
              <TypingText text="Ean Nugent" delay={1000} speed={150} />
            </Typography>
          </Fade>

          <Fade in={animationStarted} timeout={2000}>
            <Box sx={{ mb: { xs: 2, sm: 3 } }}>
              <Typography
                variant="h6"
                sx={{
                  color: '#00ffff',
                  fontFamily: '"Fira Code", monospace',
                  mb: 1,
                  opacity: 0.8,
                  fontSize: { xs: '0.8rem', sm: '1.25rem' },
                }}
              >
                $ cat role.txt
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  color: '#00ff41',
                  fontWeight: 400,
                  maxWidth: 800,
                  mx: 'auto',
                  fontFamily: '"Fira Code", monospace',
                  fontSize: { 
                    xs: '1.2rem', 
                    sm: '1.5rem', 
                    md: '2.125rem' 
                  },
                  lineHeight: { xs: 1.3, sm: 1.2 },
                  px: { xs: 1, sm: 0 },
                }}
              >
                <TypingText text="Software Engineer & AI-infused Engineering Champion" delay={2500} speed={80} />
              </Typography>
            </Box>
          </Fade>

          <Fade in={animationStarted} timeout={2500}>
            <Box sx={{ mb: { xs: 3, sm: 4 } }}>
              <Typography
                variant="h6"
                sx={{
                  color: '#00ffff',
                  fontFamily: '"Fira Code", monospace',
                  mb: 1,
                  opacity: 0.8,
                  fontSize: { xs: '0.8rem', sm: '1.25rem' },
                }}
              >
                $ echo $MISSION
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ 
                  maxWidth: { xs: '100%', sm: 600 }, 
                  mx: 'auto', 
                  lineHeight: 1.7,
                  fontFamily: '"Inter", sans-serif',
                  fontSize: { 
                    xs: '0.9rem', 
                    sm: '1rem', 
                    md: '1.25rem' 
                  },
                  px: { xs: 2, sm: 0 },
                }}
              >
                <TypingText 
                  text={isMobile 
                    ? "Positively contribute to society through innovative software systems." 
                    : "Positively contribute to society through innovative software systems."
                  }
                  delay={4000} 
                  speed={50} 
                />
              </Typography>
            </Box>
          </Fade>
        </Box>
      </Container>
    </Box>
  );
}

export default HeroSection; 